import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Rocket, Lightbulb, BarChart, Heart, Brain, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BusinessOpportunities } from '@/components/investor/BusinessOpportunities';
import CTA from '@/components/CTA';
import { supabase } from '@/integrations/supabase/client';

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    // Simulate loading time for data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    // Get the public URL for the image
    const getImageUrl = async () => {
      try {
        const { data } = supabase.storage
          .from('public-images')
          .getPublicUrl('amrish-profile.jpg');
        
        if (data?.publicUrl) {
          setImageUrl(data.publicUrl);
        }
      } catch (error) {
        console.error('Error getting image URL:', error);
      }
    };

    getImageUrl();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-16">
        {/* Hero Section - Apple Inspired */}
        <section className="py-20 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h1 className="text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Welcome to H44S
              </h1>
              <p className="text-2xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
                A platform connecting innovative entrepreneurs with forward-thinking investors. 
                Discover opportunities and grow your business with us.
              </p>
            </div>
            
            <div className="relative mx-auto max-w-5xl">
              <div className="absolute inset-0 bg-gradient-to-r from-h44s-light-blue/10 to-h44s-teal/10 rounded-[2.5rem] blur-3xl -z-10 transform -rotate-1"></div>
              <div className="bg-white/80 backdrop-blur-sm border border-gray-100 p-6 rounded-[2rem] shadow-xl overflow-hidden">
                <img 
                  src={imageUrl || '/placeholder-profile.jpg'} 
                  alt="Amrish Seunarain - A nobody wanting to help anybody while learning together" 
                  className="w-full h-[600px] object-cover rounded-[1.5rem]"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Link to="/investor">
                <Button className="px-8 py-6 text-lg bg-gradient-to-r from-h44s-light-blue to-h44s-teal hover:opacity-90 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                  Explore Opportunities
                </Button>
              </Link>
              <Link to="/about">
                <Button className="px-8 py-6 text-lg group bg-gradient-to-r from-h44s-bright-pink via-h44s-teal to-h44s-light-blue hover:opacity-90 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What's H44S About Section - Apple Inspired */}
        <section className="py-24 my-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 -skew-y-3 transform-gpu"></div>
          <div className="max-w-4xl mx-auto text-center relative z-10 px-4">
            <h2 className="text-4xl font-bold mb-6 text-gray-900 tracking-tight">What's H44S about?</h2>
            <p className="text-3xl font-medium text-indigo-600 mb-8 tracking-tight">Ever had a <span className="relative inline-block"><span className="absolute inset-x-0 bottom-0 h-3 bg-indigo-100 -z-10"></span>REALLY</span> cool idea?</p>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              I've been building for as long as I can remember. As a kid with a Nokia 3310, I would design games on paper 
              then build them in my head. It's what I've done for most of my life, and now I do it professionally.
            </p>
          </div>
        </section>

        {/* Services Grid Section - Apple Inspired */}
        <section className="py-24 my-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900 tracking-tight">How I Can Help You</h2>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {/* Card 1 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 group">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-blue-200/50 transition-all duration-300">
                  <Lightbulb className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-blue-600 transition-colors duration-300">Ignite Your Business Dreams 🔥</h3>
                <p className="text-gray-700 text-lg">
                  Have an idea but not sure how to execute it? Let's talk! I can help bring your idea to life 
                  and create the roadmap your business needs to thrive.
                </p>
              </div>
              
              {/* Card 2 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-purple-200 group">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-purple-200/50 transition-all duration-300">
                  <BarChart className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-purple-600 transition-colors duration-300">Tech Tools for Explosive Growth</h3>
                <p className="text-gray-700 text-lg">
                  I specialize in building tech tools that make businesses more efficient. From automation to custom 
                  platforms that supercharge your operation.
                </p>
              </div>
              
              {/* Card 3 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-indigo-200 group">
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-indigo-200/50 transition-all duration-300">
                  <ArrowRight className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">Design to Stand Out: Roadmaps</h3>
                <p className="text-gray-700 text-lg">
                  Launch a product people actually want to use. The key to success is good planning - from market 
                  strategy to product design and technical architecture.
                </p>
              </div>
              
              {/* Card 4 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200 group">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-green-200/50 transition-all duration-300">
                  <Heart className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-green-600 transition-colors duration-300">Build a Business You're Proud Of 🌱</h3>
                <p className="text-gray-700 text-lg">
                  I believe successful businesses should do good. We'll incorporate giving back to communities 
                  that matter to you through your business model.
                </p>
              </div>
              
              {/* Card 5 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-yellow-200 group">
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-yellow-200/50 transition-all duration-300">
                  <Brain className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-yellow-600 transition-colors duration-300">Level Up Your Mindset!</h3>
                <p className="text-gray-700 text-lg">
                  Success requires the right mindset. I'll help you cultivate a growth-oriented perspective 
                  while staying humble, giving big, and focusing on what matters.
                </p>
              </div>
              
              {/* Card 6 - Apple Inspired */}
              <div className="bg-white/90 backdrop-blur-sm p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-200 group">
                <div className="bg-gradient-to-br from-red-500 to-red-600 p-4 rounded-2xl w-fit mb-6 shadow-md group-hover:shadow-red-200/50 transition-all duration-300">
                  <Rocket className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-red-600 transition-colors duration-300">Go Global: Scale Rapidly 🚀</h3>
                <p className="text-gray-700 text-lg">
                  It's a tough job but somebody's got to do it. I can help you establish the right systems 
                  and processes to scale in a way that's sustainable.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* H44S Businesses Section - Apple Inspired */}
        <section className="py-24 my-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white/80 rounded-[3rem] mx-4"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900 tracking-tight">H44S Ventures</h2>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-6"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
                Explore our portfolio of innovative businesses, each designed to solve real-world problems 
                and create meaningful impact.
              </p>
            </div>
            
            <div className="ventures-container">
              <BusinessOpportunities />
            </div>
          </div>
        </section>
      </main>
      <CTA />
      <Footer />
    </div>
  );
};

export default Home;
